import type ComponentTypes from "~/features/cms/componentTypes";
import classNames from "classnames";
import parse, { domToReact, Element, type HTMLReactParserOptions } from "html-react-parser";
import type { CMSProps } from "~/features/cms/types/props";
import { Fragment, useEffect } from "react";
import { styled } from "@schaeffler-commerce/design-system";

export interface CMSParagraphProps extends CMSProps {
  rootStyles?: string;
  type: ComponentTypes.CMSParagraph;
}

export const CMSParagraph = styled(
  ({ content = "", className }: CMSParagraphProps) => {
    return (
      <div
        className={classNames(
          "scha__cmssection__html scha__cmssection__sub--paragraph copy_left jsp__user-generated-content",
          className,
        )}
      >
        {parse(content, options)}
      </div>
    );
  },
  { shouldForwardProp: (prop) => prop !== "rootStyles" },
)(({ rootStyles }) => rootStyles);

// 1. Define loose props to accept any attributes passed from the HTML string
interface ScriptExecuterProps {
  children?: string | null;
  [key: string]: unknown; // Allow dynamic attributes (src, async, integrity, etc.)
}

// 2. Helper Component to execute the script
const ScriptExecuter = ({ children, ...props }: ScriptExecuterProps) => {
  useEffect(() => {
    const script = document.createElement("script");

    // Copy all attributes (src, id, async, etc.)
    Object.keys(props).forEach((key) => {
      script.setAttribute(key, String(props[key]));
    });

    // If it has inline code (not a src), set the text
    if (children) {
      script.text = children;
    }

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- MOUNT-ONLY: the script executes exactly once per mount
  }, []); // Run once on mount

  return null; // Render nothing in the UI
};

/** Document-level wrappers that get unwrapped (children are hoisted). */
const DOCUMENT_WRAPPER_TAGS = new Set(["html", "head", "body"]);

/** Head-only metadata tags that get dropped (no visual content). */
const DOCUMENT_METADATA_TAGS = new Set(["meta", "title", "base", "link"]);

const options: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (!(domNode instanceof Element)) {
      return;
    }

    // Editors paste complete HTML documents into paragraph fields (observed
    // 2026-10 on /plp/CAHB10: a full page with a partcommunity iframe).
    // html-react-parser materializes html/head/body as real host elements, so
    // a nested <html> element lands inside a <div>; React 19's delegated
    // event dispatch then loops forever resolving the owning container and
    // freezes the tab. Unwrap the wrappers: hoist their children through the
    // same options so nested scripts keep executing.
    if (DOCUMENT_WRAPPER_TAGS.has(domNode.name)) {
      // domhandler's Element['children'] types a wider node union (incl. CDATA)
      // than html-dom-parser's DOMNode input; the runtime accepts both.
      return <Fragment>{domToReact(domNode.children as Parameters<typeof domToReact>[0], options)}</Fragment>;
    }

    // Document metadata has no visual value in a paragraph. A pasted <base>
    // would rewrite every relative URL on the storefront; a CMS-controlled
    // <link> would load arbitrary third-party CSS. Editors use <style> blocks.
    if (DOCUMENT_METADATA_TAGS.has(domNode.name)) {
      return <Fragment />;
    }

    // Check if the node is a script tag
    if (domNode.name === "script") {
      const props = domNode.attribs || {};
      const firstChild = domNode.children?.[0];
      const content = firstChild && "data" in firstChild ? firstChild.data : null;

      // Return our custom component that handles execution
      return <ScriptExecuter {...props}>{content}</ScriptExecuter>;
    }
  },
} as const;
